import { useEffect, useState } from "react";
import { NavLink, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { tenantApi, coaApi, type Tenant, type TenantPatch } from "../lib/tenant";
import { taxSettingsApi, type TaxRate, type WhtRule } from "../lib/taxSettings";
import { fmtCents, ncbApi, ssclApi, plansApi, billingApi, type PlanCatalogEntry } from "../lib/business";
import { trackFunnel } from "../lib/funnel";
import { api, ApiError } from "../lib/api";

export function SettingsPage() {
  return (
    <>
      <div className="stage-header">
        <div>
          <div className="stage-eyebrow">Settings</div>
          <h1 className="stage-title">
            Configure <em>your workspace.</em>
          </h1>
          <p className="stage-sub">
            Business details, tax flags, chart of accounts. Changes apply immediately to invoices,
            bills, and reports.
          </p>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "240px 1fr", gap: 28, alignItems: "start" }}>
        <nav style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <SettingsLink to="/settings/billing">Billing</SettingsLink>
          <SettingsLink to="/settings/platform">Platform</SettingsLink>
          <SettingsLink to="/settings/organisation">Organisation</SettingsLink>
          <SettingsLink to="/settings/tax">Tax flags</SettingsLink>
          <SettingsLink to="/settings/rates">Tax rates</SettingsLink>
          <SettingsLink to="/settings/wht">WHT rules</SettingsLink>
          <SettingsLink to="/settings/apit">APIT brackets</SettingsLink>
          <SettingsLink to="/settings/ncb">NCB types</SettingsLink>
          <SettingsLink to="/settings/policies">Policies</SettingsLink>
          <SettingsLink to="/settings/coa">Chart of accounts</SettingsLink>
          <SettingsLink to="/settings/invoices">Invoices</SettingsLink>
          <SettingsLink to="/settings/demo">Demo data</SettingsLink>
        </nav>
        <div>
          <Routes>
            <Route index element={<Navigate to="organisation" replace />} />
            <Route path="billing" element={<BillingSection />} />
            <Route path="platform" element={<PlatformAdminSection />} />
            <Route path="organisation" element={<OrganisationSection />} />
            <Route path="tax" element={<TaxSection />} />
            <Route path="rates" element={<RatesSection />} />
            <Route path="wht" element={<WhtSection />} />
            <Route path="apit" element={<ApitSection />} />
            <Route path="ncb" element={<NcbSection />} />
            <Route path="policies" element={<PoliciesSection />} />
            <Route path="coa" element={<CoaSection />} />
            <Route path="invoices" element={<InvoicesSection />} />
            <Route path="demo" element={<DemoSection />} />
          </Routes>
        </div>
      </div>
    </>
  );
}

function SettingsLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        "nav-item" + (isActive ? " active" : "")
      }
      style={{ textAlign: "left" }}
    >
      {children}
    </NavLink>
  );
}

function BillingSection() {
  const { data: tenant } = useQuery({ queryKey: ["tenant"], queryFn: () => tenantApi.getMe() });
  const { data: plansData } = useQuery({ queryKey: ["plans-catalog"], queryFn: () => plansApi.list() });
  const { data: billing } = useQuery({ queryKey: ["billing-history"], queryFn: () => billingApi.history() });
  const { data: receiptsData } = useQuery({ queryKey: ["billing-receipts"], queryFn: () => billingApi.receipts() });
  const { data: previewData } = useQuery({ queryKey: ["billing-preview"], queryFn: () => billingApi.preview() });
  const { data: paymentMethod, refetch: refetchPaymentMethod } = useQuery({
    queryKey: ["billing-payment-method"],
    queryFn: () => billingApi.paymentMethod(),
  });
  const [cycle, setCycle] = useState<"monthly" | "annual">("monthly");
  const [pickingPlan, setPickingPlan] = useState<"starter" | "pro" | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  const [params, setParams] = useSearchParams();

  // Honour ?upgrade=pro&cycle=annual&coupon=XYZ landing from the pricing
  // page (set by OnboardingPage when it finds a signupIntent in localStorage).
  // Redeem the coupon if any, set the cycle, then auto-fire checkout once —
  // and strip the query so a refresh doesn't loop.
  useEffect(() => {
    const upgrade = params.get("upgrade");
    if (!upgrade || (upgrade !== "starter" && upgrade !== "pro")) return;
    if (!tenant || tenant.plan !== "trial") return;
    if (redirecting) return;
    const cycleParam = params.get("cycle");
    if (cycleParam === "monthly" || cycleParam === "annual") setCycle(cycleParam);
    const couponParam = params.get("coupon");
    const next = setParams;
    (async () => {
      if (couponParam) {
        try {
          await billingApi.redeemCoupon(couponParam);
        } catch {
          /* ignore — leave coupon UX to the change-plan card */
        }
      }
      next({}, { replace: true });
      void startCheckout(upgrade);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant?.plan]);

  if (!tenant || !plansData) return <div className="card">Loading…</div>;

  const isTrial = tenant.plan === "trial";
  const currentPlan = plansData.plans.find((p) => p.key === tenant.plan);

  async function startCheckout(plan: "starter" | "pro") {
    setPickingPlan(plan);
    setRedirecting(true);
    trackFunnel("checkout_started", { plan, cycle });
    try {
      const r = await billingApi.start(plan, cycle);
      if (r.method === "payhere" && r.payhere_form && r.payhere_action) {
        // PayHere hosted checkout — auto-submit the form fields.
        const form = document.createElement("form");
        form.method = "POST";
        form.action = r.payhere_action;
        for (const [k, v] of Object.entries(r.payhere_form)) {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = k;
          input.value = v;
          form.appendChild(input);
        }
        document.body.appendChild(form);
        form.submit();
      } else {
        window.location.href = r.checkout_url;
      }
    } catch {
      setRedirecting(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="card card-pad-lg">
        <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 20, marginBottom: 4 }}>
          Billing
        </h3>
        <p style={{ color: "var(--ink-3)", fontSize: 13, marginBottom: 20 }}>
          Your current plan, what's included, and how to change it.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
          <div style={{ padding: 16, background: "var(--paper-2)", borderRadius: 8 }}>
            <div style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--ink-4)", marginBottom: 4 }}>
              Current plan
            </div>
            <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 24 }}>
              {currentPlan?.name ?? tenant.plan}
            </div>
            {isTrial && tenant.trial.days_left != null && (
              <div style={{ fontSize: 12, color: tenant.trial.days_left <= 7 ? "var(--accent-deep)" : "var(--ink-3)", marginTop: 4 }}>
                {tenant.trial.is_expired
                  ? "Trial expired"
                  : `${tenant.trial.days_left} day${tenant.trial.days_left === 1 ? "" : "s"} left`}
              </div>
            )}
            {!isTrial && tenant.plan_valid_until && (
              <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 4 }}>
                Renews {new Date(tenant.plan_valid_until).toLocaleDateString()}
              </div>
            )}
          </div>
          <div style={{ padding: 16, background: "var(--paper-2)", borderRadius: 8 }}>
            <div style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--ink-4)", marginBottom: 4 }}>
              Invoices this month
            </div>
            <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 24 }}>
              <UsageCounter limit={tenant.plan_limits.max_invoices_per_month} />
            </div>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}>
          <div className="cycle-toggle" style={{ display: "inline-flex", padding: 4, background: "var(--paper-2)", borderRadius: 999, gap: 4 }}>
            <button
              onClick={() => setCycle("monthly")}
              style={{
                padding: "8px 18px", border: "none", borderRadius: 999, cursor: "pointer",
                fontFamily: "inherit", fontSize: 13, fontWeight: 500,
                background: cycle === "monthly" ? "var(--ink)" : "transparent",
                color: cycle === "monthly" ? "var(--paper)" : "var(--ink-3)",
              }}
            >Monthly</button>
            <button
              onClick={() => setCycle("annual")}
              style={{
                padding: "8px 18px", border: "none", borderRadius: 999, cursor: "pointer",
                fontFamily: "inherit", fontSize: 13, fontWeight: 500,
                background: cycle === "annual" ? "var(--ink)" : "transparent",
                color: cycle === "annual" ? "var(--paper)" : "var(--ink-3)",
              }}
            >Annual <span style={{ fontSize: 11, color: "var(--accent-deep)", marginLeft: 4, fontWeight: 600 }}>1 month free</span></button>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
          {plansData.plans.filter((p) => p.key !== "trial").map((p) => (
            <PlanCard
              key={p.key}
              plan={p}
              cycle={cycle}
              isCurrent={p.key === tenant.plan}
              picking={pickingPlan === p.key && redirecting}
              onPick={() => p.key === "starter" || p.key === "pro" ? startCheckout(p.key) : null}
            />
          ))}
        </div>
      </div>

      {previewData?.current && (
        <NextRenewalCard
          plan={previewData.current.plan}
          billingCycle={previewData.current.billing_cycle}
          nextRenewalDate={previewData.current.next_renewal.date}
          nextRenewalGross={previewData.current.next_renewal.gross_cents}
          status={previewData.current.status}
        />
      )}

      {tenant.plan !== "trial" && tenant.plan !== "enterprise" && (
        <ChangePlanCard
          currentPlan={tenant.plan}
          currentCycle={(tenant.billing_cycle as "monthly" | "annual") ?? "monthly"}
          currentSeats={previewData?.current?.current_seats ?? previewData?.current?.seats ?? 1}
        />
      )}

      <PaymentMethodCard
        method={paymentMethod}
        onRemoved={() => refetchPaymentMethod()}
      />

      <BillingSupportCard tenantName={tenant.name} ownerEmail={tenant.email ?? null} />

      {billing?.current && billing.current.status === "active" && tenant.plan !== "trial" && (
        <CancelSubscriptionCard
          currentPlan={tenant.plan}
          periodEnd={billing.current.current_period_end}
          onCancelled={async () => {
            const qc = (await import("@tanstack/react-query")).useQueryClient;
            // Force a refetch of tenant + billing so the UI flips.
            window.location.reload();
            void qc;
          }}
        />
      )}

      {receiptsData && receiptsData.receipts.length > 0 && (
        <div className="card card-pad-lg">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
            <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 18 }}>
              Tax invoices
            </h3>
            <ReceiptsCsvExportButton />
          </div>
          <p style={{ color: "var(--ink-3)", fontSize: 13, marginBottom: 14 }}>
            IRD-compliant Tax Invoices issued by ClearAI (Pvt) Ltd for your ClearBooks subscription.
            Download for input-VAT claims, your bookkeeper, or your records.
          </p>
          <div className="table-scroll">
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={th}>Invoice #</th>
                  <th style={th}>Issued</th>
                  <th style={{ ...th, textAlign: "right" }}>Net</th>
                  <th style={{ ...th, textAlign: "right" }}>SSCL</th>
                  <th style={{ ...th, textAlign: "right" }}>VAT</th>
                  <th style={{ ...th, textAlign: "right" }}>Gross</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {receiptsData.receipts.map((r) => (
                  <tr key={r.id}>
                    <td style={td}>
                      <span className="num">{r.invoice_number}</span>
                    </td>
                    <td style={td}>{new Date(r.issue_date).toLocaleDateString()}</td>
                    <td style={{ ...td, textAlign: "right" }} className="num">{fmtCents(r.subtotal_cents)}</td>
                    <td style={{ ...td, textAlign: "right" }} className="num">{fmtCents(r.sscl_cents)}</td>
                    <td style={{ ...td, textAlign: "right" }} className="num">{fmtCents(r.vat_cents)}</td>
                    <td style={{ ...td, textAlign: "right", fontWeight: 500 }} className="num">{fmtCents(r.gross_cents)}</td>
                    <td style={td}>
                      <a
                        href={`/clearbooks/api/subscriptions/receipts/${r.id}/pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-line"
                        style={{ fontSize: 11, padding: "4px 10px", textDecoration: "none" }}
                      >
                        Download PDF
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {billing && billing.history.length > 0 && (
        <div className="card card-pad-lg">
          <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 18, marginBottom: 14 }}>
            Subscription history
          </h3>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                <th style={th}>Plan</th>
                <th style={th}>Cycle</th>
                <th style={th}>Period</th>
                <th style={{ ...th, textAlign: "right" }}>Gross</th>
                <th style={th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {billing.history.map((s) => (
                <tr key={s.id}>
                  <td style={td}>{s.plan}</td>
                  <td style={td}>{s.billing_cycle}</td>
                  <td style={td}>{new Date(s.current_period_start).toLocaleDateString()} → {new Date(s.current_period_end).toLocaleDateString()}</td>
                  <td style={{ ...td, textAlign: "right" }} className="num">{fmtCents(s.gross_cents)}</td>
                  <td style={td}>{s.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function UsageCounter({ limit }: { limit: number | null }) {
  // For now we don't fetch the real count to avoid an extra round-trip; show
  // "Unlimited" or just the cap. A future iteration could call the new
  // /reports endpoint for the actual MTD count.
  if (limit == null) return <span>Unlimited</span>;
  return <span>up to {limit}/mo</span>;
}

function PlanCard({
  plan, cycle, isCurrent, picking, onPick,
}: {
  plan: PlanCatalogEntry;
  cycle: "monthly" | "annual";
  isCurrent: boolean;
  picking: boolean;
  onPick: () => void;
}) {
  const price = cycle === "monthly" ? plan.monthly : plan.annual;
  const featured = plan.key === "pro";
  return (
    <div
      style={{
        padding: 20,
        background: featured ? "var(--paper-2)" : "var(--paper)",
        border: featured ? "1.5px solid var(--ink)" : "0.5px solid var(--line)",
        borderRadius: 12,
        display: "flex", flexDirection: "column", gap: 10,
        position: "relative",
      }}
    >
      <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 18 }}>{plan.name}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 4, minHeight: 32 }}>
        {plan.contact_sales ? (
          <span style={{ fontFamily: "'Fraunces', serif", fontSize: 22 }}>Custom</span>
        ) : price ? (
          <>
            <span style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 28 }}>
              {fmtCents(price.net_cents)}
            </span>
            <span style={{ fontSize: 12, color: "var(--ink-3)" }}>/{cycle === "monthly" ? "mo" : "yr"}</span>
          </>
        ) : null}
      </div>
      {price && (
        <div style={{ fontSize: 11, color: "var(--ink-4)" }} className="num">
          Gross: {fmtCents(price.gross_cents)}
        </div>
      )}
      <div style={{ marginTop: 4 }}>
        {isCurrent ? (
          <button className="btn" disabled style={{ width: "100%", padding: 10, background: "var(--paper-3)", color: "var(--ink-4)", cursor: "not-allowed" }}>
            Current plan
          </button>
        ) : plan.contact_sales ? (
          <a href="mailto:hello@clearai.lk?subject=ClearBooks%20Enterprise" className="btn btn-line" style={{ width: "100%", padding: 10, textAlign: "center", display: "block" }}>
            Contact sales
          </a>
        ) : (
          <button
            className="btn btn-primary"
            style={{ width: "100%", padding: 10 }}
            onClick={onPick}
            disabled={picking}
          >
            {picking ? "Redirecting…" : `Upgrade to ${plan.name}`}
          </button>
        )}
      </div>
    </div>
  );
}

function NextRenewalCard({
  plan, billingCycle, nextRenewalDate, nextRenewalGross, status,
}: {
  plan: string;
  billingCycle: string;
  nextRenewalDate: string;
  nextRenewalGross: string;
  status: string;
}) {
  return (
    <div className="card card-pad-lg">
      <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 18, marginBottom: 4 }}>
        Next renewal
      </h3>
      <p style={{ color: "var(--ink-3)", fontSize: 13, marginBottom: 18 }}>
        {status === "past_due"
          ? "Payment is past due. Update your card to avoid read-only mode."
          : "Your subscription will renew automatically on this date."}
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 16 }}>
        <KV label="Plan" value={plan} />
        <KV label="Billing" value={billingCycle === "annual" ? "Annual" : "Monthly"} />
        <KV label="Next charge" value={new Date(nextRenewalDate).toLocaleDateString()} />
        <KV label="Amount (incl. tax)" value={fmtCents(nextRenewalGross)} featured />
      </div>
    </div>
  );
}

function KV({ label, value, featured }: { label: string; value: string; featured?: boolean }) {
  return (
    <div style={{ padding: 14, background: featured ? "var(--ink)" : "var(--paper-2)", color: featured ? "var(--paper)" : "var(--ink)", borderRadius: 8 }}>
      <div style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: featured ? "var(--paper-3)" : "var(--ink-4)", marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 20 }}>{value}</div>
    </div>
  );
}

function ChangePlanCard({
  currentPlan, currentCycle, currentSeats,
}: {
  currentPlan: string;
  currentCycle: "monthly" | "annual";
  currentSeats: number;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [targetPlan, setTargetPlan] = useState<"starter" | "pro">(currentPlan === "starter" ? "pro" : "starter");
  const [targetCycle, setTargetCycle] = useState<"monthly" | "annual">(currentCycle);
  const [targetSeats, setTargetSeats] = useState<number>(currentSeats);
  const [monthsPrepaid, setMonthsPrepaid] = useState<number>(1);
  const [couponInput, setCouponInput] = useState<string>("");
  const [couponBusy, setCouponBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: preview, refetch: refetchPreview } = useQuery({
    queryKey: ["billing-preview", targetPlan, targetCycle, targetSeats, monthsPrepaid],
    queryFn: () => billingApi.preview(targetPlan, targetCycle, {
      seats: targetSeats,
      months_prepaid: targetCycle === "monthly" ? monthsPrepaid : undefined,
    }),
    enabled: open,
  });

  async function applyCoupon() {
    if (!couponInput.trim()) return;
    setCouponBusy(true); setError(null);
    try {
      await billingApi.redeemCoupon(couponInput.trim());
      setCouponInput("");
      await refetchPreview();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Coupon failed";
      setError(msg.includes("not_found") ? "Coupon not found" :
               msg.includes("expired") ? "Coupon expired" :
               msg.includes("out_of_scope") ? "Coupon doesn't apply to this account" :
               msg.includes("cap_reached") ? "Coupon has been fully redeemed" :
               msg.includes("already_active") ? "Coupon already active" : msg);
    } finally {
      setCouponBusy(false);
    }
  }

  async function removeCoupon() {
    setCouponBusy(true); setError(null);
    try {
      await billingApi.clearCoupon();
      await refetchPreview();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed");
    } finally {
      setCouponBusy(false);
    }
  }

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await billingApi.changePlan(targetPlan, targetCycle, {
        seats: targetSeats,
        months_prepaid: targetCycle === "monthly" ? monthsPrepaid : undefined,
      });
      await qc.invalidateQueries({ queryKey: ["tenant"] });
      await qc.invalidateQueries({ queryKey: ["billing-history"] });
      await qc.invalidateQueries({ queryKey: ["billing-preview"] });
      setOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card card-pad-lg">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 18, marginBottom: 4 }}>
            Change plan
          </h3>
          <p style={{ color: "var(--ink-3)", fontSize: 13 }}>
            Upgrade, downgrade, change seat count, or prepay for a discount. Changes prorate to the day.
          </p>
        </div>
        {!open && (
          <button className="btn btn-line" onClick={() => setOpen(true)}>
            Change…
          </button>
        )}
      </div>

      {open && (
        <div style={{ marginTop: 18, paddingTop: 18, borderTop: "0.5px solid var(--line)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label className="form-label">Plan</label>
              <select className="form-input filled" value={targetPlan} onChange={(e) => setTargetPlan(e.target.value as "starter" | "pro")}>
                <option value="starter">Starter</option>
                <option value="pro">Pro</option>
              </select>
            </div>
            <div>
              <label className="form-label">Billing cycle</label>
              <select className="form-input filled" value={targetCycle} onChange={(e) => setTargetCycle(e.target.value as "monthly" | "annual")}>
                <option value="monthly">Monthly</option>
                <option value="annual">Annual (1 month free)</option>
              </select>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div>
              <label className="form-label">Seats</label>
              <input
                type="number" min={1} max={500}
                className="form-input filled"
                value={targetSeats}
                onChange={(e) => setTargetSeats(Math.max(1, parseInt(e.target.value, 10) || 1))}
              />
            </div>
            {targetCycle === "monthly" && (
              <div>
                <label className="form-label">Pay for</label>
                <select className="form-input filled" value={monthsPrepaid} onChange={(e) => setMonthsPrepaid(parseInt(e.target.value, 10))}>
                  <option value={1}>1 month (no discount)</option>
                  <option value={3}>3 months (no discount)</option>
                  <option value={6}>6 months (5% off)</option>
                  <option value={12}>12 months (10% off)</option>
                </select>
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <label className="form-label">Coupon code</label>
              <input
                type="text"
                className="form-input filled"
                value={couponInput}
                placeholder={preview?.coupon ? `Active: ${preview.coupon.code}` : "Enter code"}
                onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
              />
            </div>
            {preview?.coupon ? (
              <button className="btn btn-line" onClick={removeCoupon} disabled={couponBusy}>
                Remove
              </button>
            ) : (
              <button className="btn btn-line" onClick={applyCoupon} disabled={couponBusy || !couponInput.trim()}>
                Apply
              </button>
            )}
          </div>

          {preview?.change && (
            <div style={{ padding: 16, background: "var(--paper-2)", borderRadius: 8, marginBottom: 16 }}>
              {preview.change.no_op ? (
                <div style={{ color: "var(--ink-3)", fontSize: 13 }}>You're already on this plan.</div>
              ) : (
                <>
                  <div style={{ fontSize: 12, color: "var(--ink-4)", marginBottom: 8 }}>PRORATED CHARGE TODAY</div>
                  {preview.change.today_charge ? (
                    <>
                      <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 24 }}>
                        {fmtCents(preview.change.today_charge.gross_cents)}
                      </div>
                      <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 4 }}>
                        Net {fmtCents(preview.change.today_charge.net_cents)} + SSCL {fmtCents(preview.change.today_charge.sscl_cents)} + VAT {fmtCents(preview.change.today_charge.vat_cents)}
                      </div>
                    </>
                  ) : (
                    <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 20 }}>
                      No charge today (credit applied to your next renewal)
                    </div>
                  )}
                  {preview.change.breakdown && (
                    <div style={{ marginTop: 14, paddingTop: 14, borderTop: "0.5px solid var(--line)", fontSize: 13, color: "var(--ink-3)" }}>
                      <div>Base plan: {fmtCents(preview.change.breakdown.base_plan_net_cents)} net</div>
                      {preview.change.breakdown.extra_seats > 0 && (
                        <div>
                          + {preview.change.breakdown.extra_seats} extra seat
                          {preview.change.breakdown.extra_seats === 1 ? "" : "s"} = {fmtCents(preview.change.breakdown.extra_seats_net_cents)} net
                        </div>
                      )}
                      {preview.change.breakdown.discount_pct > 0 && (
                        <div>
                          − {preview.change.breakdown.discount_pct}% multi-period discount = −{fmtCents(preview.change.breakdown.discount_net_cents)}
                        </div>
                      )}
                      {preview.change.breakdown.coupon_discount_net_cents &&
                       preview.change.breakdown.coupon_discount_net_cents !== "0" && (
                        <div>
                          − Coupon {preview.coupon?.code ?? ""} = −{fmtCents(preview.change.breakdown.coupon_discount_net_cents)}
                        </div>
                      )}
                    </div>
                  )}
                  <div style={{ marginTop: 14, paddingTop: 14, borderTop: "0.5px solid var(--line)", fontSize: 13, color: "var(--ink-3)" }}>
                    Next full period: {fmtCents(preview.change.first_full_period.gross_cents)} (incl. tax)
                  </div>
                </>
              )}
            </div>
          )}

          {error && <div className="banner banner-error" style={{ marginBottom: 12 }}>{error}</div>}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button className="btn btn-line" onClick={() => setOpen(false)} disabled={busy}>Cancel</button>
            <button
              className="btn btn-primary"
              onClick={confirm}
              disabled={busy || preview?.change?.no_op}
            >
              {busy ? "Saving…" : "Confirm change"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ReceiptsCsvExportButton() {
  const today = new Date();
  const jan1 = new Date(today.getFullYear(), 0, 1);
  const [from, setFrom] = useState(jan1.toISOString().slice(0, 10));
  const [to, setTo] = useState(today.toISOString().slice(0, 10));
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <button className="btn btn-line" style={{ fontSize: 12, padding: "6px 12px" }} onClick={() => setOpen(true)}>
        Export CSV…
      </button>
    );
  }
  const url = `/clearbooks/api/subscriptions/receipts.csv?from=${from}&to=${to}`;
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <input
        type="date"
        className="form-input filled"
        style={{ fontSize: 12, padding: "4px 8px", width: 130 }}
        value={from}
        onChange={(e) => setFrom(e.target.value)}
      />
      <span style={{ color: "var(--ink-3)", fontSize: 12 }}>to</span>
      <input
        type="date"
        className="form-input filled"
        style={{ fontSize: 12, padding: "4px 8px", width: 130 }}
        value={to}
        onChange={(e) => setTo(e.target.value)}
      />
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="btn btn-primary"
        style={{ fontSize: 12, padding: "6px 12px", textDecoration: "none" }}
        onClick={() => setOpen(false)}
      >
        Download
      </a>
      <button className="btn btn-line" style={{ fontSize: 12, padding: "6px 8px" }} onClick={() => setOpen(false)}>×</button>
    </div>
  );
}

function BillingSupportCard({ tenantName, ownerEmail }: { tenantName: string; ownerEmail: string | null }) {
  const subject = encodeURIComponent(`ClearBooks billing — ${tenantName}`);
  const body = encodeURIComponent(
    `Hi ClearAI,\n\nI'd like to talk about my ClearBooks subscription for ${tenantName}.\n\nMy question / request:\n\n— Sent from Settings → Billing.\n`,
  );
  const mailto = `mailto:billing@clearai.lk?subject=${subject}&body=${body}`;
  return (
    <div className="card card-pad-lg">
      <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 18, marginBottom: 4 }}>
        Talk to billing
      </h3>
      <p style={{ color: "var(--ink-3)", fontSize: 13, marginBottom: 14 }}>
        Need a custom plan, an extended grace period, a refund for an overcharge, or just have a question
        about your invoices? We answer within one business day.
      </p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <a href={mailto} className="btn btn-primary">Email billing</a>
        <a href="https://wa.me/94777639632" target="_blank" rel="noopener noreferrer" className="btn btn-line">WhatsApp</a>
        <a href={`tel:+94777639632`} className="btn btn-line">Call</a>
      </div>
      {ownerEmail && (
        <p style={{ marginTop: 12, color: "var(--ink-4)", fontSize: 11 }}>
          We'll reply to <strong>{ownerEmail}</strong>.
        </p>
      )}
    </div>
  );
}

function PaymentMethodCard({
  method, onRemoved,
}: {
  method: { has_method: boolean; brand?: string | null; last4?: string | null; added_at?: string | null } | undefined;
  onRemoved: () => void;
}) {
  const [busy, setBusy] = useState(false);
  if (!method) return null;
  async function remove() {
    if (!window.confirm("Remove the stored card? You'll be asked for payment details at next renewal.")) return;
    setBusy(true);
    try {
      await billingApi.removePaymentMethod();
      onRemoved();
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="card card-pad-lg">
      <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 18, marginBottom: 4 }}>
        Payment method
      </h3>
      <p style={{ color: "var(--ink-3)", fontSize: 13, marginBottom: 14 }}>
        Cards are tokenised by PayHere — we never see the full number.
      </p>
      {method.has_method ? (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: 14, background: "var(--paper-2)", borderRadius: 8 }}>
          <div>
            <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 16 }}>
              {method.brand?.toUpperCase() ?? "Card"} ending {method.last4 ?? "····"}
            </div>
            {method.added_at && (
              <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 4 }}>
                Added {new Date(method.added_at).toLocaleDateString()}
              </div>
            )}
          </div>
          <button className="btn btn-line" onClick={remove} disabled={busy}>
            {busy ? "Removing…" : "Remove"}
          </button>
        </div>
      ) : (
        <div style={{ padding: 14, background: "var(--paper-2)", borderRadius: 8, color: "var(--ink-3)", fontSize: 13 }}>
          No card on file yet. PayHere will save one automatically the next time you check out.
        </div>
      )}
    </div>
  );
}

function CancelSubscriptionCard({
  currentPlan, periodEnd, onCancelled,
}: {
  currentPlan: string;
  periodEnd: string;
  onCancelled: () => Promise<void>;
}) {
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function doCancel() {
    if (!window.confirm(
      `Cancel ${currentPlan}?\n\nYou'll keep full access until ${new Date(periodEnd).toLocaleDateString()}. After that, ClearBooks switches to read-only — you can still view + export, but new transactions are paused. Reactivate any time.`,
    )) return;
    setCancelling(true);
    setError(null);
    try {
      await billingApi.cancel();
      await onCancelled();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Cancel failed");
      setCancelling(false);
    }
  }
  return (
    <div className="card card-pad-lg" style={{ border: "0.5px solid var(--line)" }}>
      <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 18, marginBottom: 4 }}>
        Cancel subscription
      </h3>
      <p style={{ color: "var(--ink-3)", fontSize: 13, marginBottom: 14 }}>
        Cancelling keeps access through {new Date(periodEnd).toLocaleDateString()}; after that you're in read-only mode (data stays, no new transactions). Reactivate any time.
      </p>
      {error && <div className="banner banner-error" style={{ marginBottom: 12 }}>{error}</div>}
      <button
        className="btn btn-line"
        onClick={doCancel}
        disabled={cancelling}
      >
        {cancelling ? "Cancelling…" : "Cancel subscription"}
      </button>
    </div>
  );
}

function OrganisationSection() {
  const qc = useQueryClient();
  const { data: tenant, refetch } = useQuery({
    queryKey: ["tenant"],
    queryFn: () => tenantApi.getMe(),
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [draft, setDraft] = useState<Partial<Tenant>>({});

  if (!tenant) return <div className="card">Loading…</div>;

  function set<K extends keyof Tenant>(key: K, value: Tenant[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
    setSaved(false);
  }

  function v<K extends keyof Tenant>(key: K): Tenant[K] {
    return (draft[key] !== undefined ? draft[key] : tenant![key]) as Tenant[K];
  }

  async function save() {
    setSaving(true);
    try {
      const patch: TenantPatch = {
        name: v("name") as string,
        businessRegNo: (v("business_reg_no") as string | null) || null,
        address: (v("address") as string | null) || null,
        phone: (v("phone") as string | null) || null,
        email: (v("email") as string | null) || null,
        fiscalYearStartMonth: v("fiscal_year_start_month") as number,
        baseCurrency: v("base_currency") as string,
        uiLanguage: v("ui_language") as "en" | "si" | "ta",
      };
      await tenantApi.patch(patch);
      await qc.invalidateQueries({ queryKey: ["tenant"] });
      await refetch();
      setDraft({});
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card card-pad-lg">
      <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 20, marginBottom: 4 }}>
        Organisation
      </h3>
      <p style={{ color: "var(--ink-3)", fontSize: 13, marginBottom: 20 }}>
        Used on invoices, returns, and reports.
      </p>

      <div className="form-row">
        <div className="form-field">
          <label className="form-label">Business name</label>
          <input
            className="form-input filled"
            value={v("name") as string}
            onChange={(e) => set("name", e.target.value)}
          />
        </div>
        <div className="form-field">
          <label className="form-label">Business registration no.</label>
          <input
            className="form-input filled"
            value={(v("business_reg_no") as string) ?? ""}
            onChange={(e) => set("business_reg_no", e.target.value || null)}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-field">
          <label className="form-label">Address</label>
          <input
            className="form-input filled"
            value={(v("address") as string) ?? ""}
            onChange={(e) => set("address", e.target.value || null)}
          />
        </div>
        <div className="form-field">
          <label className="form-label">Phone (WhatsApp)</label>
          <input
            className="form-input filled"
            value={(v("phone") as string) ?? ""}
            onChange={(e) => set("phone", e.target.value || null)}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-field">
          <label className="form-label">Contact email</label>
          <input
            className="form-input filled"
            type="email"
            value={(v("email") as string) ?? ""}
            onChange={(e) => set("email", e.target.value || null)}
          />
        </div>
        <div className="form-field">
          <label className="form-label">UI language</label>
          <select
            className="form-input filled"
            value={v("ui_language") as string}
            onChange={(e) => set("ui_language", e.target.value as "en" | "si" | "ta")}
          >
            <option value="en">English</option>
            <option value="si">සිංහල</option>
            <option value="ta">தமிழ்</option>
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-field">
          <label className="form-label">Financial year start (month)</label>
          <input
            className="form-input filled num"
            value={String(v("fiscal_year_start_month"))}
            onChange={(e) =>
              set("fiscal_year_start_month", parseInt(e.target.value, 10) || 4)
            }
          />
        </div>
        <div className="form-field">
          <label className="form-label">Base currency</label>
          <input
            className="form-input filled"
            value={v("base_currency") as string}
            onChange={(e) => set("base_currency", e.target.value.toUpperCase())}
            maxLength={3}
          />
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        {saved && <span style={{ color: "var(--accent-deep)", fontSize: 13, alignSelf: "center" }}>Saved ✓</span>}
        <button
          className="btn btn-primary"
          onClick={save}
          disabled={saving || Object.keys(draft).length === 0}
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

function TaxSection() {
  const qc = useQueryClient();
  const { data: tenant, refetch } = useQuery({
    queryKey: ["tenant"],
    queryFn: () => tenantApi.getMe(),
  });
  const [saving, setSaving] = useState(false);

  if (!tenant) return <div className="card">Loading…</div>;

  async function toggle(field: "vat_registered" | "sscl_liable" | "payroll_active") {
    if (!tenant) return;
    setSaving(true);
    try {
      const body: TenantPatch =
        field === "vat_registered"
          ? { vatRegistered: !tenant.vat_registered }
          : field === "sscl_liable"
          ? { ssclLiable: !tenant.sscl_liable }
          : { payrollActive: !tenant.payroll_active };
      await tenantApi.patch(body);
      // Fix #21: invalidate the tenant query globally so the live-preview
      // math on the Invoices / Bills / Reports pages picks up the new flags.
      await qc.invalidateQueries({ queryKey: ["tenant"] });
      await refetch();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card card-pad-lg">
      <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 20, marginBottom: 4 }}>
        Tax
      </h3>
      <p style={{ color: "var(--ink-3)", fontSize: 13, marginBottom: 20 }}>
        Toggle what applies. Calculation engines wire on automatically.
      </p>

      <div
        className={`toggle-card ${tenant.vat_registered ? "on" : ""}`}
        onClick={() => !saving && toggle("vat_registered")}
      >
        <div>
          <div className="toggle-card-name">Business is VAT-registered</div>
          <div className="toggle-card-sub">
            Standard 18%, reduced 8%, monthly returns. Three categories: Taxable, Zero-rated,
            Exempt.
          </div>
          {tenant.vat_registered && tenant.vat_registration_no && (
            <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 6 }}>
              VAT no: <span className="num">{tenant.vat_registration_no}</span> · Filing basis:{" "}
              {tenant.vat_filing_basis}
            </div>
          )}
        </div>
        <div className={`toggle ${tenant.vat_registered ? "on" : ""}`} />
      </div>

      {tenant.vat_registered && <TaxInvoiceFields tenant={tenant} onSaved={refetch} />}

      <SsclRegistrationBanner />

      <div
        className={`toggle-card ${tenant.sscl_liable ? "on" : ""}`}
        onClick={() => !saving && toggle("sscl_liable")}
      >
        <div>
          <div className="toggle-card-name">Liable for SSCL</div>
          <div className="toggle-card-sub">
            Social Security Contribution Levy at 2.5% on services and applicable goods.
            SSCL is applied first; VAT then compounds on (base + SSCL) per the IRD.
          </div>
        </div>
        <div className={`toggle ${tenant.sscl_liable ? "on" : ""}`} />
      </div>

      <div
        className={`toggle-card ${tenant.payroll_active ? "on" : ""}`}
        onClick={() => !saving && toggle("payroll_active")}
      >
        <div>
          <div className="toggle-card-name">Run payroll &amp; file EPF/ETF/APIT</div>
          <div className="toggle-card-sub">
            Deductions, payslips, EPF C-form, ETF return, APIT schedule.
          </div>
        </div>
        <div className={`toggle ${tenant.payroll_active ? "on" : ""}`} />
      </div>

      <div className="annotation">
        <span className="annot-label">Note:</span>
        SSCL applies before VAT — your invoice will show net → SSCL → subtotal → VAT → total per the
        IRD's published order (and Gazette 2463/05 Tax Invoice format if VAT-registered).
      </div>
    </div>
  );
}

function TaxInvoiceFields({ tenant, onSaved }: { tenant: Tenant; onSaved: () => void }) {
  const [tin, setTin] = useState(tenant.tin ?? "");
  const [branch, setBranch] = useState(tenant.invoice_branch_code ?? "HQ");
  const [place, setPlace] = useState(tenant.place_of_supply ?? "");
  const [vatNo, setVatNo] = useState(tenant.vat_registration_no ?? "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const branchValid = /^[A-Za-z0-9]+$/.test(branch) && branch.length >= 1 && branch.length <= 12;
  const dirty =
    tin !== (tenant.tin ?? "") ||
    branch !== (tenant.invoice_branch_code ?? "HQ") ||
    place !== (tenant.place_of_supply ?? "") ||
    vatNo !== (tenant.vat_registration_no ?? "");

  async function save() {
    if (!branchValid) {
      setError("Branch code must be alphanumeric, 1–12 chars (e.g. HQ, BR03).");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Sanitize branch code: alphanumeric uppercase only — matches the
      // backend's regex on /tenants/me PATCH.
      const sanitizedBranch = branch.toUpperCase().replace(/[^A-Z0-9]/g, "") || "HQ";
      await tenantApi.patch({
        tin: tin || null,
        invoiceBranchCode: sanitizedBranch,
        placeOfSupply: place || null,
        vatRegistrationNo: vatNo || null,
      });
      await onSaved();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  // Live preview of the next invoice serial.
  const now = new Date();
  const yy = String(now.getUTCFullYear()).slice(-2);
  const mmm = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"][now.getUTCMonth()];
  const qqqq = branch.toUpperCase().replace(/[^A-Z0-9]/g, "") || "HQ";
  const serialPreview = `${yy}${mmm}_${qqqq}_00001`;

  return (
    <div className="card card-pad" style={{ background: "var(--paper-3)", marginBottom: 12 }}>
      <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 15, marginBottom: 4 }}>
        Tax Invoice fields
      </div>
      <div style={{ color: "var(--ink-3)", fontSize: 12, marginBottom: 16 }}>
        Printed on every Tax Invoice per Gazette 2463/05 (eff. 2026-01-01).
      </div>
      <div className="form-row">
        <div className="form-field">
          <label className="form-label">TIN (Taxpayer ID)</label>
          <input className="form-input filled num" value={tin} onChange={(e) => setTin(e.target.value)} placeholder="123 456 789 0000" />
        </div>
        <div className="form-field">
          <label className="form-label">VAT registration no.</label>
          <input className="form-input filled num" value={vatNo} onChange={(e) => setVatNo(e.target.value)} placeholder="VAT123456789" />
        </div>
      </div>
      <div className="form-row">
        <div className="form-field">
          <label className="form-label">Branch / unit code (QQQQ)</label>
          <input
            className="form-input filled"
            value={branch}
            onChange={(e) => setBranch(e.target.value)}
            maxLength={12}
            placeholder="HQ"
          />
          <div style={{ fontSize: 11, color: "var(--ink-4)", marginTop: 4 }}>
            Next serial: <span className="num">{serialPreview}</span>
          </div>
        </div>
        <div className="form-field">
          <label className="form-label">Place of supply</label>
          <input
            className="form-input filled"
            value={place}
            onChange={(e) => setPlace(e.target.value)}
            placeholder="Defaults to your business address"
          />
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8, alignItems: "center" }}>
        {error && <span style={{ color: "var(--accent-deep)", fontSize: 12 }}>{error}</span>}
        {saved && <span style={{ color: "var(--accent-deep)", fontSize: 12 }}>Saved ✓</span>}
        <button className="btn btn-primary" onClick={save} disabled={saving || !dirty || !branchValid}>
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

function SsclRegistrationBanner() {
  const { data } = useQuery({
    queryKey: ["sscl-registration-check"],
    queryFn: () => ssclApi.registrationCheck(),
  });
  if (!data) return null;
  if (!data.should_register) return null;
  return (
    <div
      className="annotation"
      style={{
        background: "#FFF4E5",
        borderColor: "#E8A53D",
        color: "#7A4500",
        marginBottom: 12,
      }}
    >
      <span className="annot-label" style={{ color: "#7A4500" }}>SSCL registration:</span>
      Your taxable turnover over the last 4 quarters has crossed an upcoming SSCL threshold.
      From {data.thresholds.effective_from} the four-quarter trigger drops to{" "}
      {fmtCents(data.thresholds.four_quarter_cents)} and the single-quarter trigger to{" "}
      {fmtCents(data.thresholds.single_quarter_cents)}. You're currently at{" "}
      {fmtCents(data.four_quarter_total_cents)} over the last 4 quarters
      {data.exceeded_single_qtr_in_window ? " and have at least one quarter above the single-quarter trigger" : ""}.
      Consider registering for SSCL.
    </div>
  );
}

function CoaSection() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["coa-mine"],
    queryFn: () => coaApi.mine(),
  });
  const [adding, setAdding] = useState(false);

  if (isLoading || !data) return <div className="card">Loading…</div>;
  type Acct = (typeof data.accounts)[number];
  const grouped = data.accounts.reduce<Record<string, Acct[]>>((acc, a) => {
    (acc[a.type] = acc[a.type] || []).push(a);
    return acc;
  }, {});

  const order = ["asset", "liability", "equity", "revenue", "expense"] as const;
  const labels: Record<string, string> = {
    asset: "Assets",
    liability: "Liabilities",
    equity: "Equity",
    revenue: "Revenue",
    expense: "Expenses",
  };

  return (
    <div className="card card-pad-lg">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 20 }}>Chart of accounts</h3>
          <p style={{ color: "var(--ink-3)", fontSize: 13 }}>
            {data?.accounts.length ?? 0} accounts. System accounts (e.g. tax payables) cannot be deleted.
          </p>
        </div>
        <button className="btn btn-line" style={{ fontSize: 12, padding: "8px 14px" }} onClick={() => setAdding(true)}>
          + New account
        </button>
      </div>

      {order.map((type) =>
        grouped[type] && grouped[type].length > 0 ? (
          <div key={type} style={{ marginBottom: 18 }}>
            <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 15, marginBottom: 8, color: "var(--ink-2)" }}>
              {labels[type]}
            </div>
            <div className="coa-preview" style={{ maxHeight: "none" }}>
              {grouped[type].map((a) => (
                <div className="coa-line" key={a.id}>
                  <span className="coa-num">{a.code}</span>
                  <span className={`coa-name ${a.code.endsWith("000") ? "bold" : ""}`}>
                    {a.name}
                    {a.statement_section && (
                      <span style={{ fontSize: 10, color: "var(--ink-4)", marginLeft: 8 }}>
                        {a.statement_section.replace(/_/g, " ")}
                      </span>
                    )}
                    {a.is_system && (
                      <span style={{ fontSize: 10, color: "var(--ink-4)", marginLeft: 8 }}>SYSTEM</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : null
      )}

      {adding && (
        <NewAccountModal
          existingCodes={data.accounts.map((a) => a.code)}
          sections={data.sections ?? STATEMENT_SECTIONS}
          onClose={() => setAdding(false)}
          onSaved={async () => {
            await qc.invalidateQueries({ queryKey: ["coa-mine"] });
            setAdding(false);
          }}
        />
      )}
    </div>
  );
}

const STATEMENT_SECTIONS = [
  "revenue", "cost_of_sales", "direct_expense", "indirect_expense",
  "other_income", "other_expense",
  "current_asset", "non_current_asset",
  "current_liability", "non_current_liability", "equity",
];

const SECTION_TO_TYPE: Record<string, "asset" | "liability" | "equity" | "revenue" | "expense"> = {
  revenue: "revenue",
  other_income: "revenue",
  cost_of_sales: "expense",
  direct_expense: "expense",
  indirect_expense: "expense",
  other_expense: "expense",
  current_asset: "asset",
  non_current_asset: "asset",
  current_liability: "liability",
  non_current_liability: "liability",
  equity: "equity",
};

function NewAccountModal({
  existingCodes, sections, onClose, onSaved,
}: { existingCodes: string[]; sections: string[]; onClose: () => void; onSaved: () => Promise<void> }) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [section, setSection] = useState<string>("revenue");
  const accountType = SECTION_TO_TYPE[section] ?? "expense";
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const codeOk = code.trim().length > 0 && !existingCodes.includes(code.trim());
  const nameOk = name.trim().length > 0;

  async function save() {
    if (!codeOk || !nameOk) {
      setError(!codeOk ? "Code is empty or already in use." : "Name is required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await coaApi.create({
        code: code.trim(),
        name: name.trim(),
        accountType,
        statementSection: section,
      });
      await onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal onClose={onClose}>
      <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 18, marginBottom: 4 }}>
        New account
      </h3>
      <p style={{ color: "var(--ink-3)", fontSize: 12, marginBottom: 16 }}>
        Pick a section; the account type is inferred. Code must be unique.
      </p>
      <div className="form-row">
        <div className="form-field"><label className="form-label">Code</label>
          <input className="form-input filled num" value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. 4140" />
        </div>
        <div className="form-field"><label className="form-label">Name</label>
          <input className="form-input filled" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Consulting income" />
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label className="form-label">Statement section</label>
          <select className="form-input filled" value={section} onChange={(e) => setSection(e.target.value)}>
            {sections.map((s) => (
              <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
            ))}
          </select>
          <div style={{ fontSize: 11, color: "var(--ink-4)", marginTop: 4 }}>
            Account type: <span className="num">{accountType}</span> (inferred)
          </div>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16, alignItems: "center" }}>
        {error && <span style={{ color: "var(--accent-deep)", fontSize: 12 }}>{error}</span>}
        <button className="btn btn-line" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={saving || !codeOk || !nameOk}>
          {saving ? "Saving…" : "Create"}
        </button>
      </div>
    </Modal>
  );
}

// ─── Tax rates ──────────────────────────────────────────────────────
function RatesSection() {
  const { data, isLoading } = useQuery({
    queryKey: ["tax-rates"],
    queryFn: () => taxSettingsApi.listRates(),
  });
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Partial<TaxRate> | null>(null);

  function refresh() {
    qc.invalidateQueries({ queryKey: ["tax-rates"] });
  }

  return (
    <div className="card card-pad-lg">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 20 }}>Tax rates</h3>
          <p style={{ color: "var(--ink-3)", fontSize: 13 }}>
            VAT 18%, 8%, 0% (zero-rated), Exempt, SSCL 2.5% pre-seeded. Add bespoke rates below;
            system rates can be deactivated but not deleted.
          </p>
        </div>
        <button className="btn btn-line" style={{ fontSize: 12, padding: "8px 14px" }} onClick={() => setEditing({})}>
          + New rate
        </button>
      </div>

      {isLoading ? <div style={{ color: "var(--ink-3)" }}>Loading…</div> : (
        <div className="table-scroll"><table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>Code</th>
              <th style={th}>Name</th>
              <th style={{ ...th, textAlign: "right" }}>Rate</th>
              <th style={th}>Claim?</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {data?.rates.map((r) => (
              <tr key={r.id} style={{ borderBottom: "0.5px solid var(--line)" }}>
                <td style={td}>
                  <span className="mono" style={{ fontSize: 12 }}>{r.code}</span>
                  {r.is_system && <span style={{ marginLeft: 8, fontSize: 10, color: "var(--ink-4)" }}>SYSTEM</span>}
                </td>
                <td style={td}>{r.name}</td>
                <td style={{ ...td, textAlign: "right", fontWeight: 500 }} className="num">{Number(r.rate_pct).toFixed(2)}%</td>
                <td style={td}>{r.is_input_claimable ? "Yes" : "No"}</td>
                <td style={{ ...td, textAlign: "right" }}>
                  <button className="btn btn-ghost" style={{ padding: "4px 10px", fontSize: 12 }} onClick={() => setEditing(r)}>Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}

      {editing && (
        <RateModal rate={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />
      )}
    </div>
  );
}

function RateModal({ rate, onClose, onSaved }: { rate: Partial<TaxRate>; onClose: () => void; onSaved: () => void }) {
  const [code, setCode] = useState(rate.code ?? "");
  const [name, setName] = useState(rate.name ?? "");
  const [ratePct, setRatePct] = useState(rate.rate_pct ?? "0");
  const [claim, setClaim] = useState(rate.is_input_claimable ?? true);
  const [active, setActive] = useState(rate.is_active ?? true);
  const [saving, setSaving] = useState(false);
  const isEdit = !!rate.id;

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        await taxSettingsApi.updateRate(rate.id!, {
          code, name, ratePct: parseFloat(ratePct), isInputClaimable: claim, isActive: active,
        });
      } else {
        await taxSettingsApi.createRate({ code, name, ratePct: parseFloat(ratePct), isInputClaimable: claim });
      }
      onSaved();
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!confirm(`Delete rate ${rate.code}?`)) return;
    await taxSettingsApi.deleteRate(rate.id!);
    onSaved();
  }

  return (
    <Modal onClose={onClose}>
      <form onSubmit={save} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 500 }}>{isEdit ? "Edit rate" : "New rate"}</div>
        <div className="form-field"><label className="form-label">Code</label>
          <input className="form-input" value={code} onChange={(e) => setCode(e.target.value)} required disabled={rate.is_system} />
        </div>
        <div className="form-field"><label className="form-label">Name</label>
          <input className="form-input" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="form-field"><label className="form-label">Rate %</label>
          <input className="form-input num" type="number" step="0.01" value={ratePct} onChange={(e) => setRatePct(e.target.value)} required />
        </div>
        <label style={{ display: "flex", gap: 8, fontSize: 13 }}>
          <input type="checkbox" checked={claim} onChange={(e) => setClaim(e.target.checked)} />
          Input VAT claimable
        </label>
        {isEdit && (
          <label style={{ display: "flex", gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            Active
          </label>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 6 }}>
          {isEdit && !rate.is_system && (
            <button type="button" className="btn btn-line" onClick={remove}>Delete</button>
          )}
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </Modal>
  );
}

// ─── WHT rules ──────────────────────────────────────────────────────
function WhtSection() {
  const { data, isLoading } = useQuery({
    queryKey: ["wht-rules"],
    queryFn: () => taxSettingsApi.listWht(),
  });
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Partial<WhtRule> | null>(null);

  return (
    <div className="card card-pad-lg">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 20 }}>WHT rules</h3>
          <p style={{ color: "var(--ink-3)", fontSize: 13 }}>
            Withholding tax instances. Defaults seeded; <em>rates and IRD references pending Shamil's confirmation</em> via the
            spec portal — adjust here when he answers.
          </p>
        </div>
        <button className="btn btn-line" style={{ fontSize: 12, padding: "8px 14px" }} onClick={() => setEditing({})}>
          + New rule
        </button>
      </div>
      {isLoading ? <div style={{ color: "var(--ink-3)" }}>Loading…</div> : (
        <div className="table-scroll"><table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>Code</th>
              <th style={th}>Trigger</th>
              <th style={{ ...th, textAlign: "right" }}>Rate</th>
              <th style={th}>IRD ref</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {data?.rules.map((r) => (
              <tr key={r.id} style={{ borderBottom: "0.5px solid var(--line)" }}>
                <td style={td}>
                  <span style={{ fontWeight: 500 }}>{r.name}</span>
                  <div style={{ fontSize: 11, color: "var(--ink-4)", fontFamily: "'JetBrains Mono', monospace" }}>{r.code}</div>
                </td>
                <td style={td}>{r.trigger_type}</td>
                <td style={{ ...td, textAlign: "right", fontWeight: 500 }} className="num">{Number(r.rate_pct).toFixed(2)}%</td>
                <td style={td}><span style={{ fontSize: 12, color: "var(--ink-3)" }}>{r.ird_reference ?? "—"}</span></td>
                <td style={{ ...td, textAlign: "right" }}>
                  <button className="btn btn-ghost" style={{ padding: "4px 10px", fontSize: 12 }} onClick={() => setEditing(r)}>Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
      {editing && (
        <WhtModal rule={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ["wht-rules"] }); }} />
      )}
    </div>
  );
}

function WhtModal({ rule, onClose, onSaved }: { rule: Partial<WhtRule>; onClose: () => void; onSaved: () => void }) {
  const [code, setCode] = useState(rule.code ?? "");
  const [name, setName] = useState(rule.name ?? "");
  const [ratePct, setRatePct] = useState(rule.rate_pct ?? "5");
  const [ird, setIrd] = useState(rule.ird_reference ?? "");
  const [trigger, setTrigger] = useState(rule.trigger_type ?? "service_to_agent");
  const [appliesTo, setAppliesTo] = useState((rule.applies_to ?? []).join(", "));
  const [suggestDefault, setSuggestDefault] = useState(rule.suggest_default ?? true);
  const [saving, setSaving] = useState(false);
  const isEdit = !!rule.id;

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        code, name,
        ratePct: parseFloat(ratePct),
        irdReference: ird || null,
        triggerType: trigger,
        appliesTo: appliesTo.split(",").map((s) => s.trim()).filter(Boolean),
        suggestDefault,
      };
      if (isEdit) await taxSettingsApi.updateWht(rule.id!, body);
      else await taxSettingsApi.createWht(body);
      onSaved();
    } finally { setSaving(false); }
  }
  async function remove() {
    if (!confirm(`Delete WHT rule ${rule.code}?`)) return;
    await taxSettingsApi.deleteWht(rule.id!);
    onSaved();
  }

  return (
    <Modal onClose={onClose}>
      <form onSubmit={save} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 500 }}>{isEdit ? "Edit WHT rule" : "New WHT rule"}</div>
        <div className="form-field"><label className="form-label">Code</label>
          <input className="form-input" value={code} onChange={(e) => setCode(e.target.value)} required />
        </div>
        <div className="form-field"><label className="form-label">Name</label>
          <input className="form-input" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="form-field"><label className="form-label">Rate %</label>
          <input className="form-input num" type="number" step="0.01" value={ratePct} onChange={(e) => setRatePct(e.target.value)} required />
        </div>
        <div className="form-field"><label className="form-label">IRD reference</label>
          <input className="form-input" placeholder="e.g. §85" value={ird} onChange={(e) => setIrd(e.target.value)} />
        </div>
        <div className="form-field"><label className="form-label">Trigger type</label>
          <select className="form-input" value={trigger} onChange={(e) => setTrigger(e.target.value)}>
            <option value="service_to_agent">Service to agent</option>
            <option value="rent">Rent</option>
            <option value="interest">Interest</option>
            <option value="dividend">Dividend</option>
            <option value="professional_fee">Professional fee</option>
          </select>
        </div>
        <div className="form-field"><label className="form-label">Applies to (comma-separated)</label>
          <input className="form-input" placeholder="service, rent, professional" value={appliesTo} onChange={(e) => setAppliesTo(e.target.value)} />
        </div>
        <label style={{ display: "flex", gap: 8, fontSize: 13 }}>
          <input type="checkbox" checked={suggestDefault} onChange={(e) => setSuggestDefault(e.target.checked)} />
          Suggest as default on matching lines
        </label>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 6 }}>
          {isEdit && !rule.is_system && <button type="button" className="btn btn-line" onClick={remove}>Delete</button>}
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </Modal>
  );
}

// ─── APIT brackets ──────────────────────────────────────────────────
function ApitSection() {
  const { data, isLoading } = useQuery({
    queryKey: ["apit"],
    queryFn: () => taxSettingsApi.listApit(),
  });

  const brackets = data?.brackets ?? [];
  type Bracket = (typeof brackets)[number];
  const grouped = brackets.reduce<Record<string, Bracket[]>>((acc, b) => {
    (acc[b.table] = acc[b.table] || []).push(b);
    return acc;
  }, {});

  return (
    <div className="card card-pad-lg">
      <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 20 }}>APIT brackets</h3>
      <p style={{ color: "var(--ink-3)", fontSize: 13, marginBottom: 14 }}>
        Monthly progressive brackets. Defaults assume LKR 1.8M/year personal relief (150,000/month).
        <em> Confirm post-April-2025 values via Shamil's spec portal.</em>
      </p>
      {isLoading ? <div style={{ color: "var(--ink-3)" }}>Loading…</div> : Object.entries(grouped).map(([table, brackets]) => (
        <div key={table} style={{ marginBottom: 22 }}>
          <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, marginBottom: 8 }}>Table {table}</div>
          <div className="table-scroll"><table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>#</th>
                <th style={{ ...th, textAlign: "right" }}>From</th>
                <th style={{ ...th, textAlign: "right" }}>To</th>
                <th style={{ ...th, textAlign: "right" }}>Rate</th>
                <th style={{ ...th, textAlign: "right" }}>Fixed deduction</th>
              </tr>
            </thead>
            <tbody>
              {brackets.map((b) => (
                <tr key={b.id} style={{ borderBottom: "0.5px solid var(--line)" }}>
                  <td style={td} className="num">{b.position}</td>
                  <td style={{ ...td, textAlign: "right" }} className="num">{fmtCents(b.from_cents)}</td>
                  <td style={{ ...td, textAlign: "right" }} className="num">{b.to_cents ? fmtCents(b.to_cents) : "—"}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 500 }} className="num">{Number(b.rate_pct).toFixed(2)}%</td>
                  <td style={{ ...td, textAlign: "right" }} className="num">{fmtCents(b.fixed_deduction_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      ))}
      <div className="annotation">
        <span className="annot-label">In-place editing of brackets</span>
        ships once Shamil confirms the canonical values via /spec. Reading is supported now so
        anyone reviewing can see what we'll calculate against.
      </div>
    </div>
  );
}

// ─── Policies (handoff §20 items moved out of Shamil's queue) ──────
function PoliciesSection() {
  const { data: tenant, refetch } = useQuery({ queryKey: ["tenant"], queryFn: () => tenantApi.getMe() });
  const [draft, setDraft] = useState<TenantPatch>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  if (!tenant) return <div className="card">Loading…</div>;

  function v<K extends keyof Tenant>(key: K): Tenant[K] {
    return draft[mapKey(key)] !== undefined
      ? (draft[mapKey(key)] as Tenant[K])
      : tenant![key];
  }

  function set(key: keyof TenantPatch, value: TenantPatch[keyof TenantPatch]) {
    setDraft((d) => ({ ...d, [key]: value }));
    setSaved(false);
  }

  async function save() {
    setSaving(true);
    try {
      await tenantApi.patch(draft);
      await refetch();
      setDraft({});
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally { setSaving(false); }
  }

  const thresholdRupees = (() => {
    const raw = v("bill_approval_threshold_cents") as string | null;
    return raw ? (Number(raw) / 100).toString() : "";
  })();
  const reliefRupees = (Number(v("personal_relief_threshold_cents") as string) / 100).toString();

  return (
    <div className="card card-pad-lg">
      <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 20 }}>Policies</h3>
      <p style={{ color: "var(--ink-3)", fontSize: 13, marginBottom: 18 }}>
        Workspace-wide defaults previously in Shamil's queue, now configurable here. Affect every
        invoice, bill, payslip, and report.
      </p>

      <div className="form-row">
        <div className="form-field">
          <label className="form-label">Bill approval threshold (LKR)</label>
          <input
            className="form-input num"
            type="number"
            placeholder="leave blank = all bills require approval"
            value={thresholdRupees}
            onChange={(e) => {
              const r = e.target.value;
              set("billApprovalThresholdCents", r === "" ? null : String(Math.round(parseFloat(r) * 100)));
            }}
          />
          <div className="form-help">Bills under this auto-pass; over still need admin sign-off.</div>
        </div>
        <div className="form-field">
          <label className="form-label">Personal relief threshold (LKR / year)</label>
          <input
            className="form-input num"
            type="number"
            value={reliefRupees}
            onChange={(e) => set("personalReliefThresholdCents", String(Math.round(parseFloat(e.target.value || "0") * 100)))}
          />
          <div className="form-help">Currently 1,800,000 (April 2025). Change as IRD updates.</div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-field">
          <label className="form-label">Payslip default channel</label>
          <select
            className="form-input"
            value={v("payslip_default_channel") as string}
            onChange={(e) => set("payslipDefaultChannel", e.target.value as TenantPatch["payslipDefaultChannel"])}
          >
            <option value="whatsapp_email">WhatsApp + Email</option>
            <option value="whatsapp">WhatsApp only</option>
            <option value="email">Email only</option>
            <option value="none">No auto-send</option>
          </select>
        </div>
        <div className="form-field">
          <label className="form-label">Cash flow report method</label>
          <select
            className="form-input"
            value={v("cash_flow_method") as string}
            onChange={(e) => set("cashFlowMethod", e.target.value as TenantPatch["cashFlowMethod"])}
          >
            <option value="direct">Direct</option>
            <option value="indirect">Indirect</option>
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-field">
          <label className="form-label">VAT reconciliation default</label>
          <select
            className="form-input"
            value={v("vat_recon_default") as string}
            onChange={(e) => set("vatReconDefault", e.target.value as TenantPatch["vatReconDefault"])}
          >
            <option value="claim">Claim this period</option>
            <option value="hold">Hold for later</option>
          </select>
          <div className="form-help">Default state for new claimable input-VAT bills.</div>
        </div>
        <div></div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        {saved && <span style={{ color: "var(--accent-deep)", fontSize: 13, alignSelf: "center" }}>Saved ✓</span>}
        <button className="btn btn-primary" onClick={save} disabled={saving || Object.keys(draft).length === 0}>
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

function mapKey(snake: keyof Tenant): keyof TenantPatch {
  const map: Record<string, keyof TenantPatch> = {
    bill_approval_threshold_cents: "billApprovalThresholdCents",
    payslip_default_channel: "payslipDefaultChannel",
    cash_flow_method: "cashFlowMethod",
    vat_recon_default: "vatReconDefault",
    personal_relief_threshold_cents: "personalReliefThresholdCents",
    name: "name",
    business_reg_no: "businessRegNo",
    address: "address",
    phone: "phone",
    email: "email",
    fiscal_year_start_month: "fiscalYearStartMonth",
    base_currency: "baseCurrency",
    ui_language: "uiLanguage",
    industry: "industry",
    vat_registered: "vatRegistered",
    vat_registration_no: "vatRegistrationNo",
    vat_filing_basis: "vatFilingBasis",
    sscl_liable: "ssclLiable",
    payroll_active: "payrollActive",
  };
  return map[snake as string] ?? (snake as never);
}

// ─── Modal helper ─────────────────────────────────────────────────
function Modal({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(14,22,18,0.5)",
        display: "grid", placeItems: "center", zIndex: 100,
      }}
      onClick={onClose}
    >
      <div className="card card-pad-lg" style={{ width: 480, maxWidth: "90vw" }} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

const th: React.CSSProperties = {
  textAlign: "left",
  padding: "10px 0",
  fontSize: 11,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "var(--ink-4)",
  fontWeight: 500,
  borderBottom: "0.5px solid var(--line)",
};
const td: React.CSSProperties = {
  padding: "10px 0",
  fontSize: 13,
  borderBottom: "0.5px solid var(--line)",
};

// ─── NCB types ────────────────────────────────────────────────────────────
function NcbSection() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["ncb-types"], queryFn: () => ncbApi.types() });
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<{
    flatCents: string;
    pct: string;
    perKmCents: string;
    capCents: string;
  }>({ flatCents: "", pct: "", perKmCents: "", capCents: "" });
  const [saving, setSaving] = useState(false);

  function startEdit(t: NcbTypeRow) {
    setEditing(t.id);
    setDraft({
      flatCents: t.flat_cents ?? "",
      pct: t.pct ?? "",
      perKmCents: t.per_km_cents ?? "",
      capCents: t.cap_cents ?? "",
    });
  }

  async function save() {
    if (!editing) return;
    setSaving(true);
    try {
      const body: Record<string, unknown> = {};
      if (draft.flatCents !== "") body.flatCents = draft.flatCents;
      if (draft.pct !== "") body.pct = parseFloat(draft.pct);
      if (draft.perKmCents !== "") body.perKmCents = draft.perKmCents;
      if (draft.capCents !== "") body.capCents = draft.capCents;
      await ncbApi.updateType(editing, body);
      qc.invalidateQueries({ queryKey: ["ncb-types"] });
      setEditing(null);
    } finally { setSaving(false); }
  }

  return (
    <div className="card card-pad-lg">
      <h2 style={{ marginBottom: 8 }}>Non-cash benefit types</h2>
      <p style={{ color: "var(--ink-3)", fontSize: 13, marginBottom: 16 }}>
        IRD circular SEC/2023/E/02. Adjust the flat amount, percentage, or per-km rate to match your policy.
        Changes take effect on the next payroll run.
      </p>

      <div className="table-scroll"><table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={tableTh}>Code</th>
            <th style={tableTh}>Name</th>
            <th style={tableTh}>Formula</th>
            <th style={{ ...tableTh, textAlign: "right" }}>Value</th>
            <th style={tableTh}>Reference</th>
            <th style={tableTh}></th>
          </tr>
        </thead>
        <tbody>
          {(data?.types as NcbTypeRow[] ?? []).map((t) => (
            <tr key={t.id}>
              <td style={tableTd}><code style={{ fontSize: 11 }}>{t.code}</code></td>
              <td style={tableTd}><strong>{t.name}</strong></td>
              <td style={{ ...tableTd, fontSize: 11, color: "var(--ink-3)" }}>{t.formula}</td>
              <td style={{ ...tableTd, textAlign: "right", fontFamily: "var(--font-mono, monospace)" }}>
                {t.flat_cents != null ? fmtCents(t.flat_cents) : ""}
                {t.pct != null && `${t.pct}%`}
                {t.per_km_cents != null && `${fmtCents(t.per_km_cents)}/km`}
                {t.cap_cents != null && ` (cap ${fmtCents(t.cap_cents)})`}
              </td>
              <td style={{ ...tableTd, fontSize: 11, color: "var(--ink-3)" }}>{t.ird_reference ?? "—"}</td>
              <td style={{ ...tableTd, textAlign: "right" }}>
                <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => startEdit(t)}>Edit</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table></div>

      {editing && (
        <div style={{ marginTop: 18, padding: 16, background: "var(--paper-2)", borderRadius: 8 }}>
          <div className="stage-eyebrow">Edit values</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 8 }}>
            <div className="form-field">
              <label className="form-label">Flat (cents)</label>
              <input className="form-input num" type="number" value={draft.flatCents}
                onChange={(e) => setDraft({ ...draft, flatCents: e.target.value })} />
            </div>
            <div className="form-field">
              <label className="form-label">Percentage</label>
              <input className="form-input num" type="number" step="0.01" value={draft.pct}
                onChange={(e) => setDraft({ ...draft, pct: e.target.value })} />
            </div>
            <div className="form-field">
              <label className="form-label">Per-km (cents)</label>
              <input className="form-input num" type="number" value={draft.perKmCents}
                onChange={(e) => setDraft({ ...draft, perKmCents: e.target.value })} />
            </div>
            <div className="form-field">
              <label className="form-label">Cap (cents)</label>
              <input className="form-input num" type="number" value={draft.capCents}
                onChange={(e) => setDraft({ ...draft, capCents: e.target.value })} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
            <button className="btn btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Local row type (fields come from /ncb/types)
type NcbTypeRow = {
  id: string;
  code: string;
  name: string;
  formula: string;
  flat_cents: string | null;
  pct: string | null;
  per_km_cents: string | null;
  cap_cents: string | null;
  ird_reference: string | null;
};

const tableTh: React.CSSProperties = {
  textAlign: "left",
  padding: "10px 8px",
  fontSize: 11,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "var(--ink-4)",
  fontWeight: 500,
  borderBottom: "0.5px solid var(--line)",
};
const tableTd: React.CSSProperties = {
  padding: "10px 8px",
  fontSize: 13,
  borderBottom: "0.5px solid var(--line)",
};

// ─── Demo data section ────────────────────────────────────────────────────
//
// Platform-admin only. Calls /v1/admin/seed-demo, which is gated server-side
// to clearai.lk / keytiqa.lk emails. If the caller isn't allowed they'll
// see a 403 and the form stays inert. Safe to leave visible.

interface SeedResult {
  ok: true;
  tenant_id: string;
  admin_email: string;
  password: string;
  counts: Record<string, number>;
}

// ─── Platform admin dashboard (clearai.lk / keytiqa.lk only) ────────────
//
// GET /v1/admin/dashboard. Server returns 403 to non-platform-admin emails;
// we surface that as a friendly inline note rather than hiding the tab.

interface DashboardSummary {
  total_tenants: number;
  trial_count: number;
  expired_trial_count: number;
  paid_count: number;
  cancelled_count: number;
  by_plan: Record<string, number>;
  mrr_net_cents: string;
}
interface DashboardRow {
  id: string;
  name: string;
  created_at: string;
  plan: string;
  plan_valid_until: string | null;
  billing_cycle: string | null;
  trial: { days_left: number; is_expired: boolean; ends_at: string } | null;
  owner_email: string | null;
  owner_name: string | null;
  active_subscription: {
    id: string;
    billing_cycle: string;
    current_period_end: string;
    gross_cents: string;
    tenant_mrr_cents: string;
    status: string;
  } | null;
}

function PlatformAdminSection() {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState<string | null>(null);
  const { data, refetch, isLoading } = useQuery({
    queryKey: ["admin-dashboard"],
    queryFn: () => api.get<{ summary: DashboardSummary; tenants: DashboardRow[] }>("/admin/dashboard"),
    retry: false,
  });

  async function extendTrial(t: DashboardRow) {
    const days = parseInt(window.prompt(`Extend trial for "${t.name}" by how many days?`, "14") ?? "", 10);
    if (!days || Number.isNaN(days) || days < 1) return;
    setActing(t.id);
    setError(null);
    try {
      await api.post(`/admin/tenants/${t.id}/extend-trial`, { days });
      await qc.invalidateQueries({ queryKey: ["admin-dashboard"] });
      await refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed");
    } finally {
      setActing(null);
    }
  }

  async function setPlan(t: DashboardRow) {
    const plan = window.prompt(`Set ${t.name}'s plan to (starter / pro / enterprise)?`, "starter");
    if (!plan || !["starter", "pro", "enterprise"].includes(plan)) return;
    const cycle = window.prompt("Billing cycle (monthly / annual)?", "monthly");
    if (!cycle || !["monthly", "annual"].includes(cycle)) return;
    setActing(t.id);
    setError(null);
    try {
      const body: Record<string, unknown> = { plan, billing_cycle: cycle, periods: 1 };
      if (plan === "enterprise") {
        const override = window.prompt("Enterprise price net (in LKR, e.g. 25000.00)?", "25000.00");
        if (!override) return;
        body.net_cents_override = Math.round(parseFloat(override) * 100);
      }
      await api.post(`/admin/tenants/${t.id}/plan`, body);
      await qc.invalidateQueries({ queryKey: ["admin-dashboard"] });
      await refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed");
    } finally {
      setActing(null);
    }
  }

  if (isLoading) return <div className="card">Loading…</div>;
  if (!data) {
    return (
      <div className="card card-pad-lg">
        <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 20, marginBottom: 4 }}>
          Platform admin
        </h3>
        <p style={{ color: "var(--ink-3)", fontSize: 13 }}>
          Platform-admin only. Sign in with a clearai.lk or keytiqa.lk email to see all tenants, MRR, and act on subscriptions.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="card card-pad-lg">
        <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 20, marginBottom: 4 }}>
          Platform overview
        </h3>
        <p style={{ color: "var(--ink-3)", fontSize: 13, marginBottom: 20 }}>
          ClearAI-wide rollup. Numbers are net (excl. SSCL + VAT).
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
          <KPI label="Tenants" value={String(data.summary.total_tenants)} />
          <KPI label="On trial" value={String(data.summary.trial_count)} sub={data.summary.expired_trial_count > 0 ? `${data.summary.expired_trial_count} expired` : undefined} />
          <KPI label="Paid" value={String(data.summary.paid_count)} />
          <KPI label="Cancelled" value={String(data.summary.cancelled_count)} />
          <KPI label="MRR (net)" value={fmtCents(data.summary.mrr_net_cents)} featured />
        </div>
      </div>

      <FunnelPanel />

      <AbTestPanel experimentKey="pricing_hero_v1" />

      <div className="card card-pad-lg">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 18 }}>All tenants</h3>
          <button className="btn btn-line" style={{ fontSize: 12, padding: "6px 12px" }} onClick={() => refetch()}>
            Refresh
          </button>
        </div>
        {error && <div className="banner banner-error" style={{ marginBottom: 12 }}>{error}</div>}
        <div className="table-scroll">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                <th style={th}>Tenant</th>
                <th style={th}>Owner</th>
                <th style={th}>Plan</th>
                <th style={th}>Status</th>
                <th style={{ ...th, textAlign: "right" }}>MRR</th>
                <th style={th}>Last seen</th>
                <th style={{ ...th, textAlign: "right" }}>Last charge</th>
                <th style={th}>Created</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {data.tenants.map((t) => (
                <tr key={t.id}>
                  <td style={td}>
                    <div style={{ fontWeight: 500 }}>{t.name}</div>
                    {t.billing_cycle && (
                      <div style={{ fontSize: 11, color: "var(--ink-4)" }}>{t.billing_cycle}</div>
                    )}
                  </td>
                  <td style={td}>
                    <div>{t.owner_name ?? "—"}</div>
                    <div style={{ fontSize: 11, color: "var(--ink-4)" }}>{t.owner_email ?? "—"}</div>
                  </td>
                  <td style={td}>
                    <span style={{ fontWeight: 500 }}>{t.plan}</span>
                  </td>
                  <td style={td}>
                    {t.trial ? (
                      <span style={{ color: t.trial.is_expired ? "var(--accent-deep)" : "var(--ink-3)" }}>
                        {t.trial.is_expired
                          ? "Trial expired"
                          : `${t.trial.days_left}d left`}
                      </span>
                    ) : t.active_subscription ? (
                      <span style={{ color: "var(--ink-3)" }}>
                        Renews {new Date(t.active_subscription.current_period_end).toLocaleDateString()}
                      </span>
                    ) : (
                      <span style={{ color: "var(--ink-4)" }}>—</span>
                    )}
                  </td>
                  <td style={{ ...td, textAlign: "right" }} className="num">
                    {t.active_subscription ? fmtCents(t.active_subscription.tenant_mrr_cents) : "—"}
                  </td>
                  <td style={td}>{new Date(t.created_at).toLocaleDateString()}</td>
                  <td style={td}>
                    <div style={{ display: "flex", gap: 6 }}>
                      {t.plan === "trial" && (
                        <button
                          className="btn btn-line"
                          style={{ fontSize: 11, padding: "4px 10px" }}
                          disabled={acting === t.id}
                          onClick={() => extendTrial(t)}
                        >
                          Extend
                        </button>
                      )}
                      <button
                        className="btn btn-primary"
                        style={{ fontSize: 11, padding: "4px 10px" }}
                        disabled={acting === t.id}
                        onClick={() => setPlan(t)}
                      >
                        {acting === t.id ? "…" : "Set plan"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

interface FunnelData {
  window: { days: number; since: string };
  unique_pricing_visitors: number;
  conversion: { tenants_registered: number; tenants_onboarded: number; tenants_paid: number };
  classic_funnel: Array<{ stage: string; count: number }>;
}

function FunnelPanel() {
  const [days, setDays] = useState(30);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin-funnel", days],
    queryFn: () => api.get<FunnelData>(`/admin/funnel?days=${days}`),
    retry: false,
  });
  if (isLoading) return <div className="card card-pad-lg">Loading funnel…</div>;
  if (!data) return null;

  const top = data.classic_funnel[0]?.count ?? 0;
  const labels: Record<string, string> = {
    pricing_view: "Pricing page views",
    pricing_cta_click: "CTA clicks",
    register_view: "Register page views",
    register_submit: "Registrations",
    onboarding_completed: "Onboarding completed",
    checkout_started: "Checkout started",
    tenants_paid: "Paid tenants",
  };
  return (
    <div className="card card-pad-lg">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
        <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 18 }}>
          Marketing funnel
        </h3>
        <div style={{ display: "flex", gap: 4 }}>
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              className="btn btn-line"
              style={{
                fontSize: 11, padding: "4px 10px",
                background: days === d ? "var(--ink)" : "transparent",
                color: days === d ? "var(--paper)" : "var(--ink)",
              }}
              onClick={() => setDays(d)}
            >{d}d</button>
          ))}
          <button className="btn btn-line" style={{ fontSize: 11, padding: "4px 10px" }} onClick={() => refetch()}>
            ↻
          </button>
        </div>
      </div>
      <p style={{ color: "var(--ink-3)", fontSize: 13, marginBottom: 18 }}>
        Last {data.window.days} days · {data.unique_pricing_visitors} unique pricing-page visitors
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {data.classic_funnel.map((s, i) => {
          const pct = top > 0 ? (s.count / top) * 100 : 0;
          const prev = i > 0 ? data.classic_funnel[i - 1].count : null;
          const dropPct = prev != null && prev > 0 ? ((prev - s.count) / prev) * 100 : null;
          return (
            <div key={s.stage} style={{ display: "grid", gridTemplateColumns: "180px 1fr 80px", gap: 12, alignItems: "center" }}>
              <div style={{ fontSize: 13 }}>{labels[s.stage] ?? s.stage}</div>
              <div style={{ position: "relative", height: 24, background: "var(--paper-2)", borderRadius: 4 }}>
                <div
                  style={{
                    position: "absolute", inset: 0, width: `${Math.max(2, pct)}%`,
                    background: "var(--ink)", borderRadius: 4,
                    transition: "width 0.2s",
                  }}
                />
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", padding: "0 10px", color: "var(--paper)", fontSize: 11, fontWeight: 500 }}>
                  {s.count.toLocaleString()}
                </div>
              </div>
              <div style={{ fontSize: 11, color: "var(--ink-3)", textAlign: "right" }}>
                {dropPct != null ? `${dropPct > 0 ? "-" : "+"}${Math.abs(dropPct).toFixed(0)}%` : "—"}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 16, paddingTop: 14, borderTop: "0.5px solid var(--line)", fontSize: 12, color: "var(--ink-3)" }}>
        Right-column shows step-over-step drop. New tenants in window: {data.conversion.tenants_registered}
        {" · "}completed onboarding: {data.conversion.tenants_onboarded}
        {" · "}paid: {data.conversion.tenants_paid}
      </div>
    </div>
  );
}

interface AbTestData {
  experiment_key: string;
  description: string;
  window: { days: number; since: string };
  control_key: string;
  variants: Array<{
    key: string;
    is_control: boolean;
    pricing_views: number;
    registrations: number;
    paid_tenants: number;
    paid_conversion_pct: number;
    lift_vs_control_pct: number;
    stages: Record<string, number>;
  }>;
}

function AbTestPanel({ experimentKey }: { experimentKey: string }) {
  const [days, setDays] = useState(30);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin-ab", experimentKey, days],
    queryFn: () => api.get<AbTestData>(`/admin/ab/${experimentKey}?days=${days}`),
    retry: false,
  });
  if (isLoading) return <div className="card card-pad-lg">Loading A/B…</div>;
  if (!data) return null;
  return (
    <div className="card card-pad-lg">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
        <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 18 }}>
          A/B test: {experimentKey}
        </h3>
        <div style={{ display: "flex", gap: 4 }}>
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              className="btn btn-line"
              style={{
                fontSize: 11, padding: "4px 10px",
                background: days === d ? "var(--ink)" : "transparent",
                color: days === d ? "var(--paper)" : "var(--ink)",
              }}
              onClick={() => setDays(d)}
            >{d}d</button>
          ))}
          <button className="btn btn-line" style={{ fontSize: 11, padding: "4px 10px" }} onClick={() => refetch()}>↻</button>
        </div>
      </div>
      <p style={{ color: "var(--ink-3)", fontSize: 13, marginBottom: 18 }}>
        {data.description}
      </p>
      <div className="table-scroll">
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr>
              <th style={th}>Variant</th>
              <th style={{ ...th, textAlign: "right" }}>Views</th>
              <th style={{ ...th, textAlign: "right" }}>Registrations</th>
              <th style={{ ...th, textAlign: "right" }}>Paid</th>
              <th style={{ ...th, textAlign: "right" }}>Conv %</th>
              <th style={{ ...th, textAlign: "right" }}>Lift vs control</th>
            </tr>
          </thead>
          <tbody>
            {data.variants.map((v) => (
              <tr key={v.key} style={{ fontWeight: v.is_control ? 400 : 500 }}>
                <td style={td}>
                  {v.key}{v.is_control && <span style={{ color: "var(--ink-4)", fontSize: 11, marginLeft: 6 }}>(control)</span>}
                </td>
                <td style={{ ...td, textAlign: "right" }} className="num">{v.pricing_views.toLocaleString()}</td>
                <td style={{ ...td, textAlign: "right" }} className="num">{v.registrations.toLocaleString()}</td>
                <td style={{ ...td, textAlign: "right" }} className="num">{v.paid_tenants.toLocaleString()}</td>
                <td style={{ ...td, textAlign: "right" }} className="num">{v.paid_conversion_pct.toFixed(2)}%</td>
                <td style={{ ...td, textAlign: "right" }} className="num">
                  {v.is_control ? "—" : (
                    <span style={{ color: v.lift_vs_control_pct > 0 ? "var(--accent-deep)" : "var(--ink-3)" }}>
                      {v.lift_vs_control_pct > 0 ? "+" : ""}{v.lift_vs_control_pct.toFixed(1)}%
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={{ marginTop: 14, color: "var(--ink-4)", fontSize: 11 }}>
        Conversion = paid tenants / pricing views. Lift compared against the control variant. Statistical significance not computed — eyeball the magnitudes and watch the trend.
      </p>
    </div>
  );
}

function KPI({ label, value, sub, featured }: { label: string; value: string; sub?: string; featured?: boolean }) {
  return (
    <div style={{ padding: 16, background: featured ? "var(--ink)" : "var(--paper-2)", color: featured ? "var(--paper)" : "var(--ink)", borderRadius: 8 }}>
      <div style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: featured ? "var(--paper-3)" : "var(--ink-4)", marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 24 }}>{value}</div>
      {sub && (
        <div style={{ fontSize: 12, color: featured ? "var(--paper-3)" : "var(--ink-3)", marginTop: 4 }}>{sub}</div>
      )}
    </div>
  );
}

// Invoice customisation (Shamil 2026-06-24).
// Lets tenants override the auto-generated invoice number prefix, paste in
// their bank details + payment instructions, and upload a logo. The logo
// goes through the same /v1/attachments upload pipeline, keyed against the
// tenant entity. PDF rendering reads tenant.invoice_logo_path / footer /
// bank / instructions and inlays them under the line items.
function InvoicesSection() {
  const qc = useQueryClient();
  const { data: tenant, refetch } = useQuery({
    queryKey: ["tenant"],
    queryFn: () => tenantApi.getMe(),
  });
  const [draft, setDraft] = useState<{
    prefix?: string;
    logoPath?: string;
    bank?: string;
    instructions?: string;
    footer?: string;
  }>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);

  if (!tenant) return <div className="card">Loading…</div>;

  const v = {
    prefix: draft.prefix !== undefined ? draft.prefix : (tenant.invoice_number_prefix ?? ""),
    logoPath: draft.logoPath !== undefined ? draft.logoPath : (tenant.invoice_logo_path ?? ""),
    bank: draft.bank !== undefined ? draft.bank : (tenant.invoice_bank_details ?? ""),
    instructions: draft.instructions !== undefined ? draft.instructions : (tenant.invoice_payment_instructions ?? ""),
    footer: draft.footer !== undefined ? draft.footer : (tenant.invoice_footer_html ?? ""),
  };

  async function uploadLogo(file: File) {
    setUploading(true);
    try {
      // Re-use the attachments router with entity_type=tenant — backend's
      // ALLOWED_ENTITIES doesn't include tenant, so we POST directly with
      // a multipart form to a dedicated /v1/tenants/me/logo route. For now,
      // store the file URL by stashing it on a known attachments key.
      const fd = new FormData();
      fd.append("file", file);
      const tok = (await import("../store/auth")).useAuthStore.getState().accessToken;
      const isProd = (import.meta as { env?: { PROD?: boolean } }).env?.PROD;
      const base = isProd ? "/clearbooks/api" : "/v1";
      const res = await fetch(`${base}/tenants/me/logo`, {
        method: "POST",
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
        body: fd,
      });
      if (!res.ok) throw new Error(await res.text());
      const json = (await res.json()) as { path: string };
      setDraft((d) => ({ ...d, logoPath: json.path }));
    } catch (e) {
      alert(`Logo upload failed: ${(e as Error).message}`);
    } finally {
      setUploading(false);
    }
  }

  async function save() {
    setSaving(true);
    try {
      await tenantApi.patch({
        invoiceNumberPrefix: v.prefix.trim() ? v.prefix.trim() : null,
        invoiceLogoPath: v.logoPath || null,
        invoiceBankDetails: v.bank || null,
        invoicePaymentInstructions: v.instructions || null,
        invoiceFooterHtml: v.footer || null,
      });
      await qc.invalidateQueries({ queryKey: ["tenant"] });
      await refetch();
      setDraft({});
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      alert(`Save failed: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card card-pad-lg">
      <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 20, marginBottom: 4 }}>
        Invoices
      </h3>
      <p style={{ color: "var(--ink-3)", fontSize: 13, marginBottom: 20 }}>
        Tailor how invoices look and are numbered. These settings apply to every invoice you raise
        from now on; previously raised invoices keep their existing number and layout.
      </p>

      <div className="form-row">
        <div className="form-field">
          <label className="form-label">Invoice number prefix</label>
          <input
            className="form-input filled"
            placeholder="e.g. INV- → invoices number INV-0001, INV-0002…"
            value={v.prefix}
            onChange={(e) => setDraft((d) => ({ ...d, prefix: e.target.value }))}
          />
          <div style={{ fontSize: 11, color: "var(--ink-4)", marginTop: 4 }}>
            Leave blank to keep the default scheme
            {tenant.vat_registered
              ? " (Gazette Tax Invoice serial YYMMM_<branch>_NNNNN)."
              : " (INV-YYYY-NNNN)."}
          </div>
        </div>

        <div className="form-field">
          <label className="form-label">Logo</label>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {v.logoPath && (
              <img
                src={v.logoPath.startsWith("/") ? v.logoPath : `/${v.logoPath}`}
                alt="logo"
                style={{ height: 40, maxWidth: 140, objectFit: "contain", border: "0.5px solid var(--line)", borderRadius: 4, padding: 4 }}
              />
            )}
            <label className="btn btn-line" style={{ cursor: "pointer" }}>
              {uploading ? "Uploading…" : (v.logoPath ? "Replace" : "Upload logo")}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                style={{ display: "none" }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadLogo(f);
                }}
              />
            </label>
            {v.logoPath && (
              <button
                type="button"
                className="btn btn-ghost"
                style={{ fontSize: 12 }}
                onClick={() => setDraft((d) => ({ ...d, logoPath: "" }))}
              >
                Remove
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="form-field" style={{ marginTop: 12 }}>
        <label className="form-label">Bank account details (printed on every invoice)</label>
        <textarea
          className="form-input filled"
          rows={4}
          placeholder={"e.g.\nAcme (Pvt) Ltd\nCommercial Bank, Colombo 03\nA/C 1234567890"}
          value={v.bank}
          onChange={(e) => setDraft((d) => ({ ...d, bank: e.target.value }))}
        />
      </div>

      <div className="form-field" style={{ marginTop: 12 }}>
        <label className="form-label">Payment instructions</label>
        <textarea
          className="form-input filled"
          rows={3}
          placeholder="e.g. Please pay within 30 days. Quote the invoice number on your transfer."
          value={v.instructions}
          onChange={(e) => setDraft((d) => ({ ...d, instructions: e.target.value }))}
        />
      </div>

      <div className="form-field" style={{ marginTop: 12 }}>
        <label className="form-label">Footer / terms (plain text)</label>
        <textarea
          className="form-input filled"
          rows={3}
          placeholder="e.g. Thank you for your business."
          value={v.footer}
          onChange={(e) => setDraft((d) => ({ ...d, footer: e.target.value }))}
        />
      </div>

      <div style={{ marginTop: 20, display: "flex", alignItems: "center", gap: 12 }}>
        <button
          className="btn btn-primary"
          onClick={save}
          disabled={saving || Object.keys(draft).length === 0}
        >
          {saving ? "Saving…" : "Save invoice settings"}
        </button>
        {saved && <span style={{ fontSize: 13, color: "var(--accent-deep)" }}>Saved.</span>}
      </div>
    </div>
  );
}

function DemoSection() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<SeedResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function runSeed() {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const data = await api.post<SeedResult>("/admin/seed-demo");
      setResult(data);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        setError("Platform-admin only. Sign in with a clearai.lk or keytiqa.lk email.");
      } else {
        setError(e instanceof Error ? e.message : "Seed failed.");
      }
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="card card-pad-lg">
      <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 20, marginBottom: 4 }}>
        Demo data
      </h3>
      <p style={{ color: "var(--ink-3)", fontSize: 13, marginBottom: 14 }}>
        Recreates the <strong>Acme Services (Pvt) Ltd</strong> demo tenant with a fresh quarter
        of activity (Apr–Jun 2026): customers, suppliers, invoices, bills, payments, payroll
        runs, and posted ledger entries. <em>Idempotent</em> — re-running wipes the demo's
        transactional data and rebuilds it.
      </p>

      <button className="btn btn-primary" onClick={runSeed} disabled={running}>
        {running ? "Seeding…" : "Re-seed demo tenant"}
      </button>

      {error && (
        <div style={{ marginTop: 14, padding: 12, background: "var(--danger-bg, #fee)", borderRadius: 8, color: "var(--danger, #c00)", fontSize: 13 }}>
          {error}
        </div>
      )}

      {result && (
        <div style={{ marginTop: 14, padding: 14, background: "var(--paper-2)", borderRadius: 8 }}>
          <div className="stage-eyebrow">Seeded</div>
          <div style={{ marginTop: 6, fontSize: 13 }}>
            Sign in as <code>{result.admin_email}</code> with password <code>{result.password}</code>
          </div>
          <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
            {Object.entries(result.counts).map(([k, v]) => (
              <div key={k} style={{ fontSize: 12 }}>
                <div style={{ color: "var(--ink-4)", textTransform: "capitalize" }}>{k}</div>
                <div style={{ fontSize: 18, fontFamily: "'Fraunces', serif" }}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
